import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link, useLocation, useHistory } from 'react-router-dom';
import { connect } from 'react-redux';
import { userActions, hairdresserActions } from '../../_actions';
import { HomeIcon, Calender, UserIcon } from '../SvgIcon';
import siteSetting from '../../config/env';
import './dashboard.css';

// Static icons defined outside component to prevent recreation on each render
const ReviewsIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
  </svg>
);

const HelpIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 17h-2v-2h2v2zm2.07-7.75l-.9.92C13.45 12.9 13 13.5 13 15h-2v-.5c0-1.1.45-2.1 1.17-2.83l1.24-1.26c.37-.36.59-.86.59-1.41 0-1.1-.9-2-2-2s-2 .9-2 2H8c0-2.21 1.79-4 4-4s4 1.79 4 4c0 .88-.36 1.68-.93 2.25z"/>
  </svg>
);

const BusinessIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M20 6h-4V4c0-1.11-.89-2-2-2h-4c-1.11 0-2 .89-2 2v2H4c-1.11 0-1.99.89-1.99 2L2 19c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2zm-6 0h-4V4h4v2z"/>
  </svg>
);

const DashboardSidebar = ({ user, dispatch, isMobileOpen, onCloseMobile }) => {
  const location = useLocation();
  const history = useHistory();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);
  
  // Use ref to track if switch is in progress to prevent race conditions on iOS
  const isSwitchingRef = useRef(false);
  
  // Memoize isActive function to prevent unnecessary re-renders
  const isActive = useCallback((path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  }, [location.pathname]);

  // Memoize handleLogout to prevent recreation on each render
  const handleLogout = useCallback(() => {
    const userId = localStorage.getItem('userId');
    if (userId) {
      dispatch(userActions.logout({ userId }))
        .then(() => {
          // Clear local storage
          localStorage.clear();
          // Redirect to home page
          history.push('/');
        })
        .catch(() => {
          // Even if logout fails, clear local storage and redirect
          localStorage.clear();
          history.push('/');
        });
    } else {
      // If no userId, just clear and redirect
      localStorage.clear();
      history.push('/');
    }
  }, [dispatch, history]);

  const toggleSidebar = useCallback(() => {
    setIsCollapsed(prev => {
      document.body.classList.toggle('client-nav-collapsed', !prev);
      return !prev;
    });
  }, []);

  useEffect(() => {
    // Clean up on unmount
    return () => {
      document.body.classList.remove('client-nav-collapsed');
    };
  }, []);

  // Memoize hairdresser check to prevent recalculation on every render
  // This is critical for iOS Safari which has smaller call stack limits
  const isHairdresserUser = useMemo(() => {
    // Primary check: user.userInfo.hairdresser (same as webHeader)
    if (user?.userInfo?.hairdresser) {
      return true;
    }
    // Fallback: check if salonId exists in localStorage (indicates user is a hairdresser)
    // Wrap in try-catch for iOS Safari compatibility
    try {
      const salonId = localStorage.getItem('salonId');
      if (salonId && salonId !== '' && salonId !== 'null' && salonId !== 'undefined') {
        return true;
      }
    } catch (e) {
      // localStorage access can fail in private browsing on iOS
      console.warn('localStorage access failed:', e);
    }
    return false;
  }, [user?.userInfo?.hairdresser]);

  // Handle switch to business profile - memoized to prevent recreation
  const handleSwitchToBusiness = useCallback((e) => {
    e.preventDefault();
    
    // Use ref to prevent race conditions on iOS
    if (isSwitchingRef.current || isSwitching) return;
    isSwitchingRef.current = true;
    setIsSwitching(true);

    const param = {
      latitude: siteSetting.default_latitude,
      longitude: siteSetting.default_longitude,
      switchTo: 1
    };

    dispatch(hairdresserActions.hairdresserSwitchProfile(param))
      .then((response) => {
        if (response.data.data.userInfo && response.data.data.userInfo.hairdresser && response.data.data.userInfo.hairdresser.salon) {
          const salonId = response.data.data.userInfo.hairdresser.salon._id;
          
          // Wrap localStorage operations in try-catch for iOS Safari
          try {
            localStorage.setItem('salonId', salonId);
            localStorage.setItem('defaultRedirectTo', response.data.data.userInfo.defaultRedirectTo);
            localStorage.setItem('isShow', response.data.data.userInfo.hairdresser.salon.isShow);

            // Check if reception mode is enabled and diary is hidden
            const cachedSettings = localStorage.getItem('receptionModeSettings');
            if (cachedSettings) {
              const { enabled, hiddenAreas } = JSON.parse(cachedSettings);
              if (enabled && hiddenAreas && hiddenAreas.diary) {
                // If diary is hidden, redirect to settings/reception-mode
                history.push('/settings/reception-mode');
                isSwitchingRef.current = false;
                setIsSwitching(false);
                return;
              }
            }
          } catch (e) {
            console.warn('localStorage operation failed:', e);
          }

          // Otherwise, redirect to diary as usual
          history.push('/diary/' + salonId);
        } else {
          console.error('No salon found for hairdresser');
          try {
            localStorage.clear();
          } catch (e) {
            console.warn('localStorage clear failed:', e);
          }
        }
        isSwitchingRef.current = false;
        setIsSwitching(false);
      })
      .catch((err) => {
        console.error('Error switching to business:', err);
        isSwitchingRef.current = false;
        setIsSwitching(false);
      });
  }, [dispatch, history, isSwitching]);

  // Memoize nav items to prevent recreation on every render
  // This is critical for preventing infinite loops on iOS Safari
  const navItems = useMemo(() => {
    const items = [
      { path: '/', icon: <HomeIcon />, label: 'Home' },
      { path: '/dashboard/bookings', icon: <Calender />, label: 'Bookings' },
      { path: '/dashboard/reviews', icon: <ReviewsIcon />, label: 'Reviews' },
      { path: '/dashboard/account', icon: <UserIcon />, label: 'Account' },
      {
        path: 'mailto:info@beuapp.com?subject=Help%20%26%20Support',
        icon: <HelpIcon />,
        label: 'Help',
        isExternal: true
      }
    ];

    // Add "Switch to Business" for hairdressers - this will be handled specially
    if (isHairdresserUser) {
      items.push({
        path: '#switch-to-business',
        icon: <BusinessIcon />,
        label: 'Switch to Business',
        isSwitchBusiness: true // Use flag instead of passing function directly
      });
    }

    return items;
  }, [isHairdresserUser]);

  // Get user initials for avatar
  const getUserInitials = () => {
    const userInfo = user?.userInfo || user;
    const userName = userInfo?.name || userInfo?.first_name;
    
    if (!userName) return 'U';
    
    const names = userName.split(' ');
    if (names.length >= 2) {
      return names[0][0] + names[names.length - 1][0];
    }
    return names[0][0];
  };

  // Get user display name
  const getUserName = () => {
    const userInfo = user?.userInfo || user;
    return userInfo?.name ||
           (userInfo?.first_name && userInfo?.last_name
             ? `${userInfo.first_name} ${userInfo.last_name}`
             : userInfo?.first_name) ||
           'User';
  };

  // Get user email
  const getUserEmail = () => {
    const userInfo = user?.userInfo || user;
    return userInfo?.email || '';
  };

  return (
    <>
      {/* Mobile Backdrop */}
      {isMobileOpen && (
        <div
          className="dashboard-mobile-backdrop active"
          onClick={onCloseMobile}
        />
      )}

      {/* Sidebar */}
      <aside className={`dashboard-sidebar ${isMobileOpen ? 'mobile-open' : ''} ${isCollapsed ? 'collapsed' : ''}`}>
        {/* Toggle Button (Desktop) */}
        <span onClick={toggleSidebar} className="dashboard-menu-toggle-btn">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z"/>
          </svg>
        </span>

        {/* Close Button (Mobile) */}
        <span onClick={onCloseMobile} className="dashboard-menu-close-btn">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
          </svg>
        </span>

        {/* Navigation */}
        <nav className="dashboard-sidebar-nav">
          <ul>
            {navItems.map((item) => (
              <li key={item.path}>
                {item.isSwitchBusiness ? (
                  // For Switch to Business, use the memoized handler
                  <a
                    href="#"
                    className={`dashboard-nav-item ${isSwitching ? 'loading' : ''}`}
                    onClick={(e) => {
                      if (!isSwitching) {
                        handleSwitchToBusiness(e);
                        onCloseMobile && onCloseMobile();
                      }
                    }}
                  >
                    <span className="dashboard-nav-icon menu_icon">{item.icon}</span>
                    {/* Label stays constant; loading state is signalled via the
                        `loading` className on the parent, not by swapping text
                        — text swap visibly shrinks the flex row mid-switch. */}
                    <span className="dashboard-nav-text">{item.label}</span>
                  </a>
                ) : item.isExternal ? (
                  // For external links (like mailto), use anchor tag
                  <a
                    href={item.path}
                    className="dashboard-nav-item"
                    onClick={onCloseMobile}
                  >
                    <span className="dashboard-nav-icon menu_icon">{item.icon}</span>
                    <span className="dashboard-nav-text">{item.label}</span>
                  </a>
                ) : (
                  // For regular navigation items, use Link
                  <Link
                    to={item.path}
                    className={`dashboard-nav-item ${isActive(item.path) ? 'active' : ''}`}
                    onClick={onCloseMobile}
                  >
                    <span className="dashboard-nav-icon menu_icon">{item.icon}</span>
                    <span className="dashboard-nav-text">{item.label}</span>
                  </Link>
                )}
              </li>
            ))}
          </ul>
        </nav>

        {/* Footer with User Profile */}
        <div className="dashboard-sidebar-footer">
          <div className="dashboard-user-profile">
            <div className="dashboard-user-avatar">
              {getUserInitials()}
            </div>
            <div className="dashboard-user-info">
              <p className="dashboard-user-name">
                {getUserName()}
              </p>
              <p className="dashboard-user-email">
                {getUserEmail()}
              </p>
            </div>
            <button
              className="dashboard-logout-btn"
              onClick={handleLogout}
              title="Logout"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z"/>
              </svg>
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};

function mapStateToProps(state) {
  // Get the full user object from state to access user.userInfo.hairdresser
  const { user } = state;
  
  return {
    user
  };
}

export default connect(mapStateToProps)(DashboardSidebar);